import { DXF_MAPPING_VALIDATION_CODE } from '@frontend/api';

import {
  createEntityActions,
  disabled,
  enabled,
  hidden,
  hiddenAction,
  type ActionAvailability,
  type EntityAction,
  type EntityActionDefinition,
} from 'modules/common/entityActions';
import type { ViewerAccess } from 'modules/common/viewer/hostContracts/access';
import type { ViewerEditLocks } from 'modules/common/viewer/hostContracts/editLocks';
import {
  getPartEditAvailability,
  PART_EDIT_LOADING_REASON,
} from 'modules/common/viewer/partEditPolicy';

/**
 * A DXF file's mapping: the role overlay that says what is cut, etched and bent.
 * It has no id of its own; it is reached through its part file.
 */
export type DxfMappingSubject = {
  partFileUuid: string;
  /** A mapping row exists on the server, so saving updates rather than creates. */
  hasSaved: boolean;
  hasUnsavedChanges: boolean;
  /**
   * The backend's verdict on the current draft. `pending` keeps the last known
   * issue codes while a check is in flight; whether to wait on it is transient
   * state, left to the binding.
   */
  validation:
    | { status: 'failed' }
    | { status: 'pending' | 'checked'; issueCodes: readonly string[] };
};

/** A draft quote a split's line items can land in. */
export type DxfMappingSplitQuote = {
  id: number;
  number: number;
  revisionNumber: number | null;
};

/**
 * Where a split's line items land, or why there is nowhere: one fact, so a host
 * cannot pass a quote and a reason that disagree.
 */
export type DxfMappingQuoteToSplitTo =
  | { status: 'available'; quote: DxfMappingSplitQuote }
  | { status: 'unavailable'; reason?: string };

export const availableQuoteToSplitTo = (
  quote: DxfMappingSplitQuote,
): DxfMappingQuoteToSplitTo => ({ status: 'available', quote });

/** `reason` is shown when the host knows why it has no quote. */
export const unavailableQuoteToSplitTo = (
  reason?: string | null,
): DxfMappingQuoteToSplitTo =>
  reason ? { status: 'unavailable', reason } : { status: 'unavailable' };

/** Facts about the user and the viewer, gathered by the composition root. */
export type DxfMappingActionContext = {
  access: ViewerAccess;
  editLocks: ViewerEditLocks;
  /**
   * The quote this viewer acts in, passed in by the host page; unavailable when
   * it has none (the part library viewer, or a file not on exactly one draft
   * quote), with the reason when the host knows it.
   */
  quoteToSplitTo: DxfMappingQuoteToSplitTo;
};

export const DXF_MAPPING_SAVE_BLOCK = {
  FAILED: 'Something went wrong',
  ISSUES: "Can't save mapping with validation issues",
  PENDING: "Can't save mapping while validation is pending",
  NO_CHANGES: 'No changes to save',
} as const;

export const DXF_MAPPING_SPLIT_BLOCK = {
  // NB: We should never need to show this to the user. This is basically a something went wrong error.
  NO_QUOTE: "Can't split without an associated quote",
  PENDING: "Can't split while validation is pending",
} as const;

/**
 * Whether this mapping may be changed at all: the part edit rule, then the file's
 * geometry lock, which is what the backend's `MappingEditable` enforces (it also
 * covers nesting and shared files). Shared by every mapping action, and by the
 * composition root as the session's lock, so the two cannot disagree.
 */
export const getDxfMappingEditAvailability = (
  ctx: DxfMappingActionContext,
): ActionAvailability => {
  const partEditAvailability = getPartEditAvailability(
    ctx.access,
    ctx.editLocks,
  );
  if (partEditAvailability.status !== 'enabled') {
    return partEditAvailability;
  }
  const { geometryLock } = ctx.editLocks;
  if (geometryLock.status === 'loading') {
    return disabled(PART_EDIT_LOADING_REASON);
  }
  if (geometryLock.status === 'locked') {
    return disabled(`Cannot edit this mapping because ${geometryLock.reason}.`);
  }
  return enabled();
};

/** Enters mapping mode, to create or change the mapping. */
export const dxfMappingEditDefinition: EntityActionDefinition<
  DxfMappingSubject,
  DxfMappingActionContext
> = {
  id: 'dxfMapping.edit',
  label: 'Edit mapping',
  selection: 'single',
  decide: (_mapping, ctx) => getDxfMappingEditAvailability(ctx),
};

/** Makes the draft durable. Nothing reaches the server until this runs. */
export const dxfMappingSaveDefinition: EntityActionDefinition<
  DxfMappingSubject,
  DxfMappingActionContext
> = {
  id: 'dxfMapping.save',
  label: 'Save mapping',
  selection: 'single',
  decide: (mapping, ctx) => {
    const editAvailability = getDxfMappingEditAvailability(ctx);
    if (editAvailability.status !== 'enabled') {
      return editAvailability;
    }
    if (mapping.validation.status === 'failed') {
      return disabled(DXF_MAPPING_SAVE_BLOCK.FAILED);
    }
    if (mapping.validation.issueCodes.length > 0) {
      return disabled(DXF_MAPPING_SAVE_BLOCK.ISSUES);
    }
    // A mapping never saved is worth saving even untouched (the auto-mapping).
    if (mapping.hasSaved && !mapping.hasUnsavedChanges) {
      return disabled(DXF_MAPPING_SAVE_BLOCK.NO_CHANGES);
    }
    return enabled();
  },
};

/**
 * Splits a file that draws several flat parts into one line item per part, the
 * way out when the mapping cannot be saved for having more than one outer cut
 * contour. Applies only to a mapping the backend flagged for exactly that.
 */
export const dxfMappingSplitToLineItemsDefinition: EntityActionDefinition<
  DxfMappingSubject,
  DxfMappingActionContext
> = {
  id: 'dxfMapping.splitToLineItems',
  label: 'Split to line items',
  selection: 'single',
  decide: (mapping, ctx) => {
    if (mapping.validation.status === 'failed') {
      return hidden('The mapping could not be validated');
    }
    if (
      !mapping.validation.issueCodes.includes(
        DXF_MAPPING_VALIDATION_CODE.MULTIPLE_OUTER_CUT_CONTOURS,
      )
    ) {
      return hidden('The mapping draws a single flat part');
    }
    // Splitting only resolves having several flat patterns. With anything else
    // wrong too, it is not yet the way on: those come first.
    if (
      mapping.validation.issueCodes.some(
        (code) =>
          code !== DXF_MAPPING_VALIDATION_CODE.MULTIPLE_OUTER_CUT_CONTOURS,
      )
    ) {
      return hidden('The mapping has issues splitting would not resolve');
    }
    const editAvailability = getDxfMappingEditAvailability(ctx);
    if (editAvailability.status !== 'enabled') {
      return editAvailability;
    }
    if (ctx.quoteToSplitTo.status === 'unavailable') {
      return disabled(
        ctx.quoteToSplitTo.reason ?? DXF_MAPPING_SPLIT_BLOCK.NO_QUOTE,
      );
    }
    return enabled();
  },
};

export type DxfMappingActions = {
  edit: EntityAction<DxfMappingSubject>;
  save: EntityAction<DxfMappingSubject>;
  splitToLineItems: EntityAction<DxfMappingSubject>;
};

export const DxfMappingActionsEntity = createEntityActions<DxfMappingActions>({
  name: 'DxfMapping',
  fallback: () => ({
    edit: hiddenAction(dxfMappingEditDefinition),
    save: hiddenAction(dxfMappingSaveDefinition),
    splitToLineItems: hiddenAction(dxfMappingSplitToLineItemsDefinition),
  }),
});

export const useDxfMappingActions = DxfMappingActionsEntity.useActions;
